/**
 * The Invoices list (design/prototype/Invoices.dc.html): every invoice in one
 * of four groups — needs action (drafts, ready to send, needs attention, and
 * anything overdue), waiting for payment, paid, and closed (void or written
 * off: no money expected, D-033) — with what happens next and four numbers on top. Overdue means sent longer ago than the business rules
 * allow and still owing. Pure.
 */
import { z } from 'zod'
import type { BusinessRules } from '../business-rules/rules.ts'
import { csvLine } from '../mcp/exports.ts'
import { money } from '../shared/money.ts'
import { dayKey, shortDay, shortDayOf } from '../shared/zoned.ts'
import { awaitsUnpaidCheck, type Invoice, invoiceSubtotal, isClosed, isOverdue } from './invoice.ts'

export const invoiceGroups = ['action', 'waiting', 'paid', 'closed', 'all'] as const
export type InvoiceGroup = (typeof invoiceGroups)[number]

export const invoiceGroupLabels: Record<InvoiceGroup, string> = {
  action: 'Needs action',
  waiting: 'Waiting for payment',
  paid: 'Paid',
  closed: 'Void or written off',
  all: 'All',
}

/** The same five tones every Majik pill uses (features/shared/components/tone-pill). */
export type InvoiceTone = 'info' | 'success' | 'pending' | 'neutral' | 'danger'

export interface InvoicesContext {
  now: string
  rules: Pick<
    BusinessRules,
    'timeZone' | 'overdueAfterDays' | 'paymentReminderDays' | 'salesTaxRate' | 'goLive'
  >
  clientNames: ReadonlyMap<string, string>
  locationNames: ReadonlyMap<string, string>
  /** When each location's next monthly invoice goes out. */
  nextBillDates: ReadonlyMap<string, string | null>
  /** Clients with at least one billing contact who has an email — anyone to send to. */
  billable: ReadonlySet<string>
  /** Clients whose payment reminders are paused (Billing setup ⋯ Pause reminders). */
  remindersPaused: ReadonlySet<string>
}

export interface InvoiceRow {
  id: string
  clientId: string
  locationId: string | null
  /** "Another Tomorow - 137 Wooster Store" — the location the invoice is for. */
  where: string
  clientName: string
  /** "October" or "One-off 09-18-2026". */
  what: string
  number: string
  sent: string
  status: Invoice['status']
  statusLabel: string
  tone: InvoiceTone
  /** "Reminder 2 on Oct 31", "Paid Oct 6", "Add a billing contact". */
  next: string
  total: number
  /** Still owed on a sent invoice; null when nothing is. */
  owed: number | null
  group: Exclude<InvoiceGroup, 'all'>
  overdue: boolean
  /** Imported from Monday as unpaid and not yet confirmed: no reminders, one line on Today. */
  importedUnpaid: boolean
  /** The invoice's place in time: when sent, or made. */
  at: string
  paidAt: string | null
}

const DAY = 86_400_000

/** Including tax: QuickBooks' total once it has one, else the lines plus tax if taxable. */
export function invoiceTotal(
  invoice: Pick<Invoice, 'lines' | 'quickbooksTotal' | 'taxCode'>,
  salesTaxRate: number,
): number {
  if (invoice.quickbooksTotal !== null) return invoice.quickbooksTotal
  const subtotal = invoiceSubtotal(invoice)
  const tax = invoice.taxCode === 'tax' ? (subtotal * salesTaxRate) / 100 : 0
  return Math.round((subtotal + tax) * 100) / 100
}

/** Days since it was sent, in whole days. */
export function daysSince(at: string, now: string): number {
  return Math.floor((Date.parse(now) - Date.parse(at)) / DAY)
}

/** The next payment reminder still to come, or null when they are all done. */
export function nextReminder(
  sentAt: string,
  now: string,
  reminderDays: readonly number[],
): { number: number; at: string } | null {
  const age = daysSince(sentAt, now)
  const index = reminderDays.findIndex((d) => d > age)
  if (index < 0) return null
  const days = reminderDays[index] ?? 0
  return { number: index + 1, at: new Date(Date.parse(sentAt) + days * DAY).toISOString() }
}

/**
 * Which of the list's groups an invoice is in at `now`: closed (void or
 * written off), needs action (unsent, or overdue), paid, or waiting. One
 * rule, shared by the rows and the counts (Postgres counts it in SQL —
 * invoice-groups.repository.drizzle.ts).
 */
export function invoiceGroupOf(
  invoice: Pick<Invoice, 'status' | 'lastSentAt' | 'balanceDue'>,
  now: string,
  overdueAfterDays: number,
): Exclude<InvoiceGroup, 'all'> {
  if (isClosed(invoice.status)) return 'closed'
  const unsent =
    invoice.status === 'draft' ||
    invoice.status === 'ready_to_send' ||
    invoice.status === 'needs_attention'
  if (unsent || isOverdue(invoice, now, overdueAfterDays)) return 'action'
  return invoice.status === 'paid' ? 'paid' : 'waiting'
}

export function invoiceRow(invoice: Invoice, ctx: InvoicesContext): InvoiceRow {
  const tz = ctx.rules.timeZone
  const [where = invoice.name, label = ''] = invoice.name.split(' | ')
  const overdue = isOverdue(invoice, ctx.now, ctx.rules.overdueAfterDays)
  const total = invoiceTotal(invoice, ctx.rules.salesTaxRate)
  const sentAt = invoice.lastSentAt
  const unsent =
    invoice.status === 'draft' ||
    invoice.status === 'ready_to_send' ||
    invoice.status === 'needs_attention'
  const nextBill =
    invoice.type === 'monthly' && invoice.locationId
      ? ctx.nextBillDates.get(invoice.locationId)
      : null
  const reminder = sentAt ? nextReminder(sentAt, ctx.now, ctx.rules.paymentReminderDays) : null
  const importedUnpaid = awaitsUnpaidCheck(invoice)
  const remind = importedUnpaid
    ? 'Imported — check it’s unpaid'
    : ctx.remindersPaused.has(invoice.clientId)
      ? 'Reminders paused'
      : reminder
        ? `Reminder ${reminder.number} on ${shortDay(reminder.at, tz)}`
        : 'Reminders done — call them'

  let statusLabel: string
  let tone: InvoiceTone
  let next: string
  if (invoice.status === 'paid') {
    statusLabel = 'Paid'
    tone = 'success'
    next = invoice.paidAt ? `Paid ${shortDay(invoice.paidAt, tz)}` : 'Paid'
  } else if (isClosed(invoice.status)) {
    const void_ = invoice.status === 'void'
    statusLabel = void_ ? 'Void' : 'Uncollectible'
    // Neutral, not red: nothing is wrong now, the invoice has simply ended.
    tone = 'neutral'
    const on = invoice.closedAt ? ` ${shortDay(invoice.closedAt, tz)}` : ''
    next = `${void_ ? 'Voided' : 'Written off'}${on}`
  } else if (invoice.status !== 'draft' && unsent && !ctx.billable.has(invoice.clientId)) {
    statusLabel = 'Needs attention'
    // Amber, not red: red is for errors and deleting (docs/UX-GUIDE.md, D-024).
    tone = 'pending'
    next = 'Add a billing contact, then send'
  } else if (invoice.status === 'ready_to_send' || invoice.status === 'needs_attention') {
    statusLabel = 'Ready to send'
    tone = 'info'
    next = nextBill ? `Goes out ${shortDayOf(nextBill)}` : 'Send when ready'
  } else if (invoice.status === 'draft') {
    statusLabel = 'Draft'
    tone = 'neutral'
    next = nextBill ? `Review, goes out ${shortDayOf(nextBill)}` : 'Review, then send'
  } else if (importedUnpaid) {
    // Not "N days late": it may well be paid in QuickBooks already.
    statusLabel = 'Imported unpaid'
    tone = 'pending'
    next = remind
  } else if (overdue && sentAt) {
    statusLabel = `${daysSince(sentAt, ctx.now)} days late`
    tone = 'pending'
    next = remind
  } else {
    statusLabel = 'Sent'
    tone = 'pending'
    next = remind
  }

  const owed = invoice.status === 'sent' ? (invoice.balanceDue ?? total) : null
  return {
    id: invoice.id,
    clientId: invoice.clientId,
    locationId: invoice.locationId,
    where,
    clientName: ctx.clientNames.get(invoice.clientId) ?? '',
    what: /^\d/.test(label) ? `One-off ${label}` : label,
    number: invoice.invoiceNumber ?? 'not in QuickBooks yet',
    sent: sentAt ? shortDay(sentAt, tz) : '—',
    status: invoice.status,
    statusLabel,
    tone,
    next,
    total,
    owed: owed && owed > 0 ? owed : null,
    group: invoiceGroupOf(invoice, ctx.now, ctx.rules.overdueAfterDays),
    overdue,
    importedUnpaid,
    at: sentAt ?? invoice.createdAt,
    paidAt: invoice.paidAt,
  }
}

/** Unsent first, then oldest sent first — what needs chasing rises to the top. Paid: newest payment first. */
export function sortRows(rows: readonly InvoiceRow[], group: InvoiceGroup): InvoiceRow[] {
  const unsent = (r: InvoiceRow) => (r.sent === '—' ? 0 : 1)
  // Ties by the newest first, then the higher record number — the order the
  // invoices are listed in — so a tie reads the same from either store.
  const tie = (a: InvoiceRow, b: InvoiceRow) => b.id.localeCompare(a.id)
  return [...rows].sort((a, b) =>
    group === 'paid'
      ? (b.paidAt ?? '').localeCompare(a.paidAt ?? '') || b.at.localeCompare(a.at) || tie(a, b)
      : unsent(a) - unsent(b) || a.at.localeCompare(b.at) || tie(a, b),
  )
}

/** The four numbers above the list. */
export function invoiceStats(
  rows: readonly InvoiceRow[],
  ctx: Pick<InvoicesContext, 'now' | 'rules'>,
) {
  const tz = ctx.rules.timeZone
  const sum = (list: readonly InvoiceRow[], f: (r: InvoiceRow) => number) =>
    Math.round(list.reduce((s, r) => s + f(r), 0) * 100) / 100
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
  const sent = rows.filter((r) => r.status === 'sent')
  const overdue = rows.filter((r) => r.overdue)
  const toReview = rows.filter((r) => r.group === 'action' && !r.overdue)
  const month = dayKey(ctx.now, tz).slice(0, 7)
  const lastMonthDate = new Date(Date.parse(`${month}-01T12:00:00Z`) - 5 * DAY).toISOString()
  const lastMonth = lastMonthDate.slice(0, 7)
  const paidIn = (m: string) => rows.filter((r) => r.paidAt && dayKey(r.paidAt, tz).startsWith(m))
  const monthName = (at: string) => shortDay(at, tz).split(' ')[0]
  return [
    {
      key: 'waiting' as const,
      label: 'Owed to Majik',
      value: money(sum(sent, (r) => r.owed ?? 0)),
      note: plural(sent.length, 'sent invoice'),
    },
    {
      key: 'action' as const,
      label: 'Overdue',
      value: money(sum(overdue, (r) => r.owed ?? 0)),
      note: `${plural(overdue.length, 'invoice')} over ${ctx.rules.overdueAfterDays} days`,
    },
    {
      key: 'action' as const,
      label: 'To review and send',
      value: money(sum(toReview, (r) => r.total)),
      note: `${plural(toReview.length, 'invoice')} not sent yet`,
    },
    {
      key: 'paid' as const,
      label: `Paid in ${monthName(ctx.now)}`,
      value: money(sum(paidIn(month), (r) => r.total)),
      note: `${plural(paidIn(month).length, 'payment')} · ${money(sum(paidIn(lastMonth), (r) => r.total))} in ${monthName(lastMonthDate)}`,
    },
  ]
}

export const invoicesQuerySchema = z.object({
  group: z.enum(invoiceGroups).default('action'),
  q: z.string().trim().max(100).default(''),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  /** `unpaid`: only invoices imported as unpaid and not yet confirmed (Today's link). */
  imported: z.enum(['unpaid']).optional(),
})
export type InvoicesQuery = z.infer<typeof invoicesQuerySchema>

/** The rows a group and a search show: client, location, month or invoice number. */
export function filterRows(
  rows: readonly InvoiceRow[],
  query: Pick<InvoicesQuery, 'group' | 'q'> & Partial<Pick<InvoicesQuery, 'imported'>>,
) {
  const q = query.q.toLowerCase()
  return sortRows(
    rows.filter(
      (r) =>
        (!query.imported || r.importedUnpaid) &&
        (query.group === 'all' || r.group === query.group) &&
        (!q || `${r.where} ${r.clientName} ${r.what} ${r.number}`.toLowerCase().includes(q)),
    ),
    query.group,
  )
}

export function invoicesCsv(rows: readonly InvoiceRow[]): string {
  // csvLine quotes what needs it and defuses a cell Excel would run as a formula.
  const head = [
    'Invoice',
    'For',
    'Client',
    'Number',
    'Sent',
    'Status',
    'Next step',
    'Total',
    'Still owed',
  ]
  const lines = rows.map((r) =>
    csvLine([
      r.where,
      r.what,
      r.clientName,
      r.number,
      r.sent,
      r.statusLabel,
      r.next,
      Math.round(r.total * 100) / 100,
      r.owed === null ? '' : Math.round(r.owed * 100) / 100,
    ]),
  )
  return [csvLine(head), ...lines].join('')
}
